<script setup lang="ts">
import { computed, ref } from 'vue'
import { useScreen } from '@/composables/useScreen'

const { isMobile } = useScreen()

const open = ref(false)
const tab = ref(0)
interface ParentStudent {
  id: number
  name: string
  reports: Array<{ jcourse_id: number; jcourse_name: string; units: Array<{ name: string }> }>
  diplomas: Array<{ jdiploma_id: number; name: string; url: string }>
}

const student = ref<ParentStudent | null>(null)

function $open(studentValue: ParentStudent) {
  open.value = true
  student.value = studentValue
}

const reports = computed(() => {
  if (!student.value) return []

  const result = []

  for (const report of student.value.reports) {
    let name = `${report.jcourse_name} `

    if (report.units.length === 1) name += `(${report.units[0].name})`
    if (report.units.length === 2) name += `(${report.units[0].name}, ${report.units[1].name})`
    if (report.units.length > 2) {
      name += `(${report.units[0].name} - ${report.units[report.units.length - 1].name})`
    }

    const url = `/journal/student_report_parent/${report.jcourse_id}/${student.value.id}`

    result.push({ name, url, id: report.jcourse_id })
  }

  return result
})

const diplomas = computed(() => {
  if (!student.value) return []

  return student.value.diplomas.map(e => ({
    name: e.name,
    url: e.url,
    id: e.jdiploma_id
  }))
})

const filesList = computed(() => {
  if (tab.value === 0) return reports.value
  if (tab.value === 1) return diplomas.value
  return []
})

defineExpose({
  $open
})
</script>

<template>
  <v-modal v-model="open" title="Отчёты" class="overflow-modal">
    <div v-if="student" class="student-report">
      <h2 v-if="!isMobile" class="pr-3 text-h3">Отчеты и дипломы</h2>

      <div class="d-flex">
        <v-buttons-group v-model="tab" mode="inactive" class="mt-d-2 mb-2">
          <v-button mode="primary">Отчеты ({{ student.reports.length }})</v-button>
          <v-button mode="primary">Дипломы ({{ student.diplomas.length }})</v-button>
        </v-buttons-group>
      </div>

      <p v-if="tab === 0" class="my-1">
        Здесь можно посмотреть отчёты об успеваемости ребёнка
        <strong>{{ student.name }}</strong>
      </p>

      <p v-if="tab === 1" class="my-1">
        Здесь можно посмотреть дипломы ребёнка
        <strong>{{ student.name }}</strong>
      </p>

      <a v-for="file in filesList" :key="file.id" class="student-report__file" :href="file.url" target="_blank">
        <v-file :name="file.name" ext="pdf" />
        <div class="flex-spacer" />
        <div class="student-report__iconbtn">
          <v-icon>mdi-open-in-new</v-icon>
        </div>
      </a>
    </div>
  </v-modal>
</template>

<style lang="scss">
.overflow-modal .v-modal__content {
  @include desktop {
    overflow-y: visible;
  }
}

.student-report {
  display: flex;
  flex-direction: column;

  @include desktop {
    width: 500px;
    max-height: 450px;

    & > p {
      max-width: 90%;
    }
  }

  @include mobile {
    height: 100%;
  }

  &__files-container {
    overflow-y: auto;
  }

  &__file {
    display: flex;
    align-items: center;
    cursor: pointer;

    padding: 4px 28px 4px 20px;
    border-radius: 8px;
    border: 1px solid $gray;

    margin: 8px 0;

    & > div.v-file {
      margin-bottom: 0 !important;
      width: auto !important;

      font-weight: bold;

      &:hover {
        background: none;
      }
    }
  }

  &__iconbtn {
    color: $blue;
    cursor: pointer;
  }
}
</style>
